import React from "react";
import api from "@/lib/api";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import {
  X, Gear, Sparkle, PaperPlaneRight,
  User as UserIcon, Robot, Paperclip,
  Plus, CaretDown, Trash, Receipt,
  CheckCircle, WarningCircle, PencilSimple,
  ArrowLeft, FloppyDisk, FilePdf, ArrowClockwise,
} from "@phosphor-icons/react";
import { useErp } from "@/context/ErpContext";
import WizardFields from "@/components/erp/WizardFields";

// ── Constants ─────────────────────────────────────────────────────────────────
const COMMAND_KEYWORDS = new Set([
  "done", "done_editing", "done editing", "confirm", "review",
  "edit", "cancel", "back", "populate", "populate fields",
  "populate_fields", "review_edit", "review/edit",
]);
function isNavCommand(text) {
  return COMMAND_KEYWORDS.has((text || "").trim().toLowerCase());
}

// FIX 3: Fallback only — real options always come from backend next_field_ui
const RECEIPT_METHOD_FALLBACK = [
  "Bank Transfer Method",
  "Cash Method",
  "Cheque Method",
  "Credit/Debit Card",
  "LC Method",
  "ORA_AP/AR Netting",
];

const CHAT_TO_WIZARD = {
  business_unit:                 "business_unit",
  receipt_method:                "receipt_method",
  customer_reference_number:     "customer_reference_number",
  currency:                      "currency",
  entered_amount:                "entered_amount",
  receipt_amount:                "receipt_amount",
  receipt_date:                  "receipt_date",
  accounting_date:               "accounting_date",
  remittance_bank_name:          "remittance_bank_name",
  remittance_bank_branch:        "remittance_bank_branch",
  remittance_bank_account:       "remittance_bank_account",
  remittance_bank_account_number:"remittance_bank_account_number",
  remittance_bank_deposit_date:  "remittance_bank_deposit_date",
  receipt_number:                "receipt_number",
  document_number:               "document_number",
  comments:                      "comments",
  amount:                        "entered_amount",
  remittance_amount:             "entered_amount",
  customer:                      "customer_reference_number",
  customer_name:                 "customer_reference_number",
  remitter:                      "customer_reference_number",
  reference_number:              "customer_reference_number",
  ref_number:                    "customer_reference_number",
  transaction_number:            "customer_reference_number",
  remitting_bank:                "remittance_bank_name",
  bank_reference:                "remittance_bank_account",
};

const REQUIRED_RECEIPT_FIELDS = [
  "business_unit",
  "receipt_method",
  "customer_reference_number",
  "currency",
  "entered_amount",
  "receipt_date",
  "accounting_date",
  "remittance_bank_name",
  "remittance_bank_branch",
  "remittance_bank_account",
  "remittance_bank_deposit_date",
  "receipt_number",
];

const FIELD_LABELS = {
  receipt_number:     "Receipt Number",
  receipt_date:       "Receipt Date",
  receipt_amount:     "Receipt Amount",
  amount:             "Amount",
  remittance_amount:  "Remittance Amount",
  currency:           "Currency",
  customer_name:      "Customer Name",
  remitter:           "Remitter",
  reference_number:   "Reference Number",
  ref_number:         "Reference Number",
  transaction_number: "Transaction Number",
  invoice_number:     "Invoice Number",
  receipt_method:     "Receipt Method",
  remitting_bank:     "Remitting Bank",
  bank_reference:     "Bank Reference",
  exchange_rate:      "Exchange Rate",
  tax_amount:         "Tax Amount",
  credit_account:     "Credit Account",
  credit_value_date:  "Credit Value Date",
  remittance_info:    "Remittance Info",
};

const DISPLAY_ORDER = [
  "receipt_number", "receipt_date", "customer_name", "remitter",
  "receipt_amount", "amount", "remittance_amount", "currency",
  "reference_number", "ref_number", "transaction_number",
  "invoice_number", "receipt_method", "remitting_bank", "bank_reference",
  "exchange_rate", "tax_amount", "credit_account", "credit_value_date",
];

const ACCEPTED_DOC_TYPES = [
  "Bank Credit Advice", "FCY Inward Remittance", "Payment Receipt",
  "Remittance Advice", "Customer Payment Confirmation",
];

function ts() {
  return new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
}

function normalizeFieldValue(field, raw) {
  if (!raw && raw !== 0) return raw;
  const s = String(raw).trim();
  if (["receipt_date", "accounting_date", "remittance_bank_deposit_date"].includes(field)) {
    const formats = [
      { re: /^(\d{4})-(\d{2})-(\d{2})$/, fn: (m) => `${m[1]}-${m[2]}-${m[3]}` },
      { re: /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/, fn: (m) => `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}` },
      { re: /^(\d{1,2})-(\d{1,2})-(\d{4})$/, fn: (m) => `${m[3]}-${m[2].padStart(2,"0")}-${m[1].padStart(2,"0")}` },
      { re: /^(\d{1,2})-([A-Za-z]{3})-(\d{4})$/, fn: (m) => {
          const months = {jan:"01",feb:"02",mar:"03",apr:"04",may:"05",jun:"06",
                         jul:"07",aug:"08",sep:"09",oct:"10",nov:"11",dec:"12"};
          return `${m[3]}-${months[m[2].toLowerCase()]||"00"}-${m[1].padStart(2,"0")}`;
        }
      },
    ];
    for (const { re, fn } of formats) {
      const m = s.match(re);
      if (m) {
        try { const norm = fn(m); if (norm && !norm.includes("00")) return norm; } catch { }
      }
    }
    return "";
  }
  if (field === "entered_amount") {
    const n = parseFloat(s.replace(/,/g, ""));
    return isNaN(n) ? s : String(n);
  }
  if (field === "currency") return s.toUpperCase();
  return s;
}

const PLACEHOLDER_VALUES = new Set([
  "", "null", "undefined", "(not provided)", "(not extracted)",
  "__attachment_uploaded__", "attachment_uploaded", "__show_options__",
  "__execute_oracle__", "__reask_confirm__", "none", "n/a",
]);

function isMissingValue(val) {
  if (val === null || val === undefined) return true;
  const s = String(val).trim().toLowerCase();
  return PLACEHOLDER_VALUES.has(s);
}

function orderedKeys(data) {
  const all = [
    ...DISPLAY_ORDER.filter((k) => data[k] !== undefined && data[k] !== null && data[k] !== ""),
    ...Object.keys(data).filter(
      (k) => !DISPLAY_ORDER.includes(k) &&
             !["confidence", "is_receipt", "validation_reason"].includes(k) &&
             data[k] !== undefined && data[k] !== null && data[k] !== ""
    ),
  ];
  const seen = new Set();
  return all.filter((k) => {
    const label = FIELD_LABELS[k] || k;
    if (seen.has(label)) return false;
    seen.add(label);
    return true;
  });
}

function formatAmount(value) {
  if (!value) return "—";
  const num = parseFloat(String(value).replace(/,/g, ""));
  if (isNaN(num)) return String(value);
  return num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

// ══════════════════════════════════════════════════════════════════════════════
// STEP 2 — Receipt Attachment Upload
// ══════════════════════════════════════════════════════════════════════════════
function ReceiptAttachmentUpload({ sessionId, onUploaded, onValidationFail }) {
  const [status, setStatus] = React.useState("idle");
  const [fileName, setFileName] = React.useState(null);
  const [errorMsg, setErrorMsg] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setStatus("uploading");
    setErrorMsg(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(
        `/erp-agents/receipt-create/${sessionId}/upload-receipt-attachment`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const isReceipt = data?.is_receipt !== false;
      const extraction = data?.extraction || data?.fields_collected || {};
      const hasReceiptFields =
        extraction.receipt_number || extraction.receipt_amount ||
        extraction.amount || extraction.remitter ||
        extraction.remittance_amount || extraction.reference_number;
      if (!isReceipt || (!hasReceiptFields && data?.is_receipt === false)) {
        setStatus("invalid");
        const reason = data?.validation_reason ||
          `⚠️ **"${file.name}"** does not appear to be a receipt document.\n\n` +
          `Accepted types:\n` + ACCEPTED_DOC_TYPES.map((t) => `• ${t}`).join("\n") +
          `\n\nPlease upload a clearer scan or a PDF version.`;
        setErrorMsg(reason);
        onValidationFail?.(reason);
        return;
      }
      setStatus("success");
      onUploaded?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      setStatus("error");
      setErrorMsg(msg);
    }
  };

  const handleFileChange = (e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };
  const reset = () => { setStatus("idle"); setFileName(null); setErrorMsg(null); };

  if (status === "success") return (
    <div className="border border-green-200 bg-green-50 rounded-lg p-3 flex items-center gap-3">
      <CheckCircle size={20} weight="fill" className="text-green-600 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-bold text-green-800">Receipt document uploaded</p>
        <p className="text-[11px] text-green-600 truncate">{fileName}</p>
      </div>
    </div>
  );
  if (status === "invalid" || status === "error") return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2">
      <div className="flex items-start gap-2">
        <WarningCircle size={16} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-amber-800 mb-1">
            {status === "invalid" ? "Invalid document type" : "Upload failed"}
          </p>
          <p className="text-[11px] text-amber-700 whitespace-pre-line">{errorMsg || "Please try again."}</p>
        </div>
      </div>
      <button onClick={reset} className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 flex items-center gap-1">
        <ArrowClockwise size={12} weight="bold" /> Try another file
      </button>
    </div>
  );
  if (status === "uploading") return (
    <div className="border border-[#0020C2]/20 bg-blue-50/60 rounded-lg p-4 flex items-center gap-3">
      <span className="w-5 h-5 border-2 border-[#0020C2]/30 border-t-[#0020C2] rounded-full animate-spin shrink-0" />
      <div>
        <p className="text-xs font-bold text-[#0020C2]">Processing document…</p>
        <p className="text-[11px] text-gray-500 truncate">{fileName}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">Running OCR and field extraction</p>
      </div>
    </div>
  );
  return (
    <div className={`border-2 border-dashed rounded-lg p-4 transition-colors cursor-pointer
        ${dragOver ? "border-[#0020C2] bg-[#0020C2]/5" : "border-gray-200 hover:border-[#0020C2]/50 hover:bg-gray-50/60"}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}>
      <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
        className="hidden" onChange={handleFileChange} />
      <div className="flex flex-col items-center text-center gap-2">
        <div className="w-10 h-10 rounded-full bg-[#0020C2]/10 flex items-center justify-center">
          <Paperclip size={20} weight="bold" className="text-[#0020C2]" />
        </div>
        <div>
          <p className="text-sm font-bold text-gray-800">Upload Receipt Document</p>
          <p className="text-xs text-gray-500 mt-0.5">Drag and drop or click to browse</p>
        </div>
        <div className="text-[10px] text-gray-400 space-y-0.5">
          <p>Accepted: {ACCEPTED_DOC_TYPES.slice(0, 3).join(" · ")} + more</p>
          <p>Formats: PDF, Word, Excel, JPG, PNG</p>
        </div>
        <div className="flex items-center gap-1.5 text-[10px] text-[#0020C2] bg-[#0020C2]/5 px-3 py-1 rounded-full">
          <FilePdf size={11} weight="bold" /> Fields will be extracted automatically
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STEP 4 — Extraction Summary
// ══════════════════════════════════════════════════════════════════════════════
function ReceiptExtractionSummary({ data, businessUnit, onPopulate, onReviewEdit }) {
  if (!data) return null;
  const keys = orderedKeys(data);
  return (
    <div className="border border-[#0020C2]/20 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="bg-[#0020C2] px-4 py-3 flex items-center gap-2">
        <Receipt size={16} weight="bold" className="text-white/80" />
        <span className="text-xs font-black tracking-[0.15em] uppercase text-white">Receipt Extraction Summary</span>
      </div>
      <div className="h-px bg-[#0020C2]/10" />
      {businessUnit && (
        <div className="px-4 py-2 border-b border-gray-100 flex items-center justify-between">
          <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wide">Business Unit</span>
          <span className="text-[11px] font-mono font-bold text-gray-800">{businessUnit}</span>
        </div>
      )}
      <div className="divide-y divide-gray-50">
        {keys.length === 0 && (
          <div className="px-4 py-3 text-xs text-gray-400 text-center">No fields could be extracted.</div>
        )}
        {keys.map((key) => (
          <div key={key} className="px-4 py-2 flex items-center justify-between gap-4">
            <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wide shrink-0">
              {FIELD_LABELS[key] || key.replace(/_/g, " ")}
            </span>
            <span className="text-[11px] font-mono text-gray-800 text-right">
              {key.includes("amount") || key === "exchange_rate"
                ? formatAmount(data[key])
                : String(data[key] || "—")}
            </span>
          </div>
        ))}
      </div>
      <div className="px-4 py-2 bg-gray-50 border-t border-gray-100 flex items-center justify-between">
        <span className="text-[10px] text-gray-400">{keys.length} field{keys.length !== 1 ? "s" : ""} extracted</span>
        {data.confidence && (
          <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full
            ${data.confidence === "high" ? "bg-green-100 text-green-700" :
              data.confidence === "medium" ? "bg-amber-100 text-amber-700" : "bg-red-100 text-red-700"}`}>
            {data.confidence} confidence
          </span>
        )}
      </div>
      <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
        <button onClick={onPopulate}
          className="flex items-center justify-center gap-1.5 px-3 py-3 bg-[#0020C2] text-white text-xs font-bold hover:bg-[#001AA3] transition-colors">
          <CheckCircle size={14} weight="bold" /> Populate Fields
        </button>
        <button onClick={onReviewEdit}
          className="flex items-center justify-center gap-1.5 px-3 py-3 text-[#0020C2] text-xs font-bold hover:bg-[#0020C2]/5 transition-colors">
          <PencilSimple size={14} weight="bold" /> Review / Edit
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STEP 6 — Review / Edit
// ══════════════════════════════════════════════════════════════════════════════
function ReceiptReviewEditor({ data, onDone, onBack }) {
  const [localData, setLocalData] = React.useState({ ...data });
  const [editingKey, setEditingKey] = React.useState(null);
  const keys = orderedKeys(localData);
  const handleSave = (key, value) => {
    if (isNavCommand(value)) return;
    setLocalData((prev) => ({ ...prev, [key]: value }));
    setEditingKey(null);
  };
  return (
    <div className="border border-gray-200 rounded-xl overflow-hidden bg-white shadow-sm">
      <div className="bg-gray-800 px-4 py-3 flex items-center gap-2">
        <PencilSimple size={14} weight="bold" className="text-white/70" />
        <span className="text-xs font-black tracking-[0.15em] uppercase text-white">Review &amp; Edit Fields</span>
      </div>
      <div className="p-3 space-y-1">
        <p className="text-[11px] text-gray-500 mb-2">Click any field to edit. Press Enter to save, Esc to cancel.</p>
        {keys.map((key) => {
          const label = FIELD_LABELS[key] || key.replace(/_/g, " ");
          if (editingKey === key) {
            return (
              <FieldEditor key={key} fieldKey={key} label={label} value={localData[key]}
                onSave={handleSave} onCancel={() => setEditingKey(null)} />
            );
          }
          return (
            <div key={key}
              className="flex items-center justify-between gap-3 px-3 py-2 rounded-lg hover:bg-[#0020C2]/5 cursor-pointer group transition-colors"
              onClick={() => setEditingKey(key)}>
              <div className="min-w-0 flex-1">
                <div className="text-[10px] font-bold uppercase tracking-wider text-gray-500">{label}</div>
                <div className="text-sm text-gray-800 font-mono truncate">
                  {localData[key] || <span className="text-gray-300 italic">empty</span>}
                </div>
              </div>
              <PencilSimple size={13} weight="bold" className="shrink-0 text-gray-300 group-hover:text-[#0020C2] transition-colors" />
            </div>
          );
        })}
        {keys.length === 0 && <p className="text-xs text-gray-400 text-center py-4">No fields to edit.</p>}
      </div>
      <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
        <button onClick={onBack}
          className="flex items-center justify-center gap-1.5 px-3 py-3 text-gray-600 text-xs font-bold hover:bg-gray-50 transition-colors">
          <ArrowLeft size={13} weight="bold" /> Back to Summary
        </button>
        <button onClick={() => onDone?.(localData)}
          className="flex items-center justify-center gap-1.5 px-3 py-3 bg-[#0020C2] text-white text-xs font-bold hover:bg-[#001AA3] transition-colors">
          <CheckCircle size={13} weight="bold" /> Done Editing
        </button>
      </div>
    </div>
  );
}

function FieldEditor({ fieldKey, label, value, onSave, onCancel }) {
  const [draft, setDraft] = React.useState(value || "");
  const [error, setError] = React.useState(null);
  const inputRef = React.useRef(null);
  React.useEffect(() => { inputRef.current?.focus(); inputRef.current?.select(); }, []);
  const save = () => {
    if (isNavCommand(draft)) { setError(`"${draft}" is a reserved command.`); return; }
    setError(null);
    onSave(fieldKey, draft.trim());
  };
  return (
    <div className="border border-[#0020C2]/30 rounded-lg overflow-hidden bg-white">
      <div className="px-3 py-1.5 bg-[#0020C2]/5 border-b border-[#0020C2]/10">
        <span className="text-[10px] font-bold uppercase tracking-wider text-[#0020C2]">Editing: {label}</span>
      </div>
      <div className="p-3 space-y-2">
        <input ref={inputRef} value={draft}
          onChange={(e) => { setDraft(e.target.value); setError(null); }}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); save(); } if (e.key === "Escape") onCancel(); }}
          className={`w-full text-sm border rounded px-3 py-2 outline-none font-mono focus:ring-2 focus:ring-[#0020C2]/20 focus:border-[#0020C2] ${error ? "border-red-400" : "border-gray-200"}`}
          placeholder={`Enter ${label.toLowerCase()}…`} />
        {error && <p className="text-[11px] text-red-600">{error}</p>}
        <div className="flex gap-2">
          <button onClick={save}
            className="flex items-center gap-1 text-xs font-bold px-3 py-1.5 bg-[#0020C2] text-white rounded hover:bg-[#001AA3] transition-colors">
            <FloppyDisk size={12} weight="bold" /> Save
          </button>
          <button onClick={onCancel}
            className="text-xs font-bold px-3 py-1.5 border border-gray-200 rounded text-gray-600 hover:bg-gray-50 transition-colors">
            Cancel
          </button>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// STEP 8 — Final Summary
// ══════════════════════════════════════════════════════════════════════════════
function ReceiptSummary({ values, onConfirm, onReviewEdit, receiptStatus, oracleResult }) {
  if (!values) return null;
  const SUMMARY_ORDER = [
    { key: "business_unit",                label: "Business Unit" },
    { key: "receipt_number",               label: "Receipt Number" },
    { key: "receipt_date",                 label: "Receipt Date" },
    { key: "receipt_method",               label: "Receipt Method" },
    { key: "customer_reference_number",    label: "Customer Reference" },
    { key: "currency",                     label: "Currency" },
    { key: "entered_amount",               label: "Amount" },
    { key: "accounting_date",              label: "Accounting Date" },
    { key: "remittance_bank_name",         label: "Remittance Bank Name" },
    { key: "remittance_bank_branch",       label: "Remittance Bank Branch" },
    { key: "remittance_bank_account",      label: "Oracle Bank Account #" },
    { key: "remittance_bank_deposit_date", label: "Remittance Bank Deposit Date" },
    { key: "comments",                     label: "Comments" },
  ];
  const rows = SUMMARY_ORDER.filter((f) => {
    const val = values[`${f.key}__label`] || values[f.key];
    if (!val) return false;
    const s = String(val);
    if (isMissingValue(val)) return false;
    if (s.toUpperCase().includes("ATTACHMENT_UPLOADED")) return false;
    if (s.startsWith("__") && s.endsWith("__")) return false;
    return true;
  });
  const isSubmitting = receiptStatus === "submitting";
  const isFailed     = receiptStatus === "failed";
  return (
    <div className="border border-[#0020C2]/20 rounded-xl overflow-hidden bg-white shadow-sm my-2">
      <div className="bg-[#0020C2] px-4 py-3 flex items-center gap-2">
        <Receipt size={16} weight="bold" className="text-white/80" />
        <span className="text-xs font-black tracking-[0.15em] uppercase text-white">AR Receipt Summary</span>
        {isSubmitting && (
          <span className="ml-auto text-[10px] text-white/70 flex items-center gap-1">
            <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin inline-block" />
            Creating…
          </span>
        )}
      </div>
      {isFailed && (
        <div className="px-4 py-2 bg-red-50 border-b border-red-200 flex items-center gap-2">
          <WarningCircle size={14} weight="fill" className="text-red-500 shrink-0" />
          <span className="text-xs text-red-700 font-medium">Receipt creation failed. Fix any issues and try again.</span>
        </div>
      )}
      <div className="border-b-2 border-[#0020C2]/30" />
      <div className="divide-y divide-gray-50">
        {rows.map((f) => (
          <div key={f.key} className="px-4 py-2 flex items-center justify-between gap-4">
            <span className="text-[11px] text-gray-500 font-medium uppercase tracking-wide shrink-0 w-40">{f.label}</span>
            <span className="text-[11px] font-mono text-gray-800 font-semibold text-right">
              {values[`${f.key}__label`] || values[f.key]}
            </span>
          </div>
        ))}
      </div>
      <div className="border-t-2 border-[#0020C2]/30" />
      <div className="px-4 py-3 bg-blue-50/40 text-center">
        <p className="text-xs text-gray-600 font-medium">
          Type <code className="font-mono font-bold text-[#0020C2] bg-[#0020C2]/10 px-1.5 py-0.5 rounded">CONFIRM</code> or click <strong>Create Receipt</strong> to proceed.
        </p>
      </div>
      <div className="grid grid-cols-2 divide-x divide-gray-200 border-t border-gray-200">
        <button onClick={isSubmitting ? undefined : onConfirm}
          disabled={isSubmitting}
          className={"flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-bold transition-colors "
            + (isSubmitting
              ? "bg-gray-300 text-gray-500 cursor-not-allowed"
              : "bg-[#0020C2] text-white hover:bg-[#001AA3]")}>
          {isSubmitting
            ? <><span className="w-3 h-3 border-2 border-gray-400 border-t-gray-600 rounded-full animate-spin" /> Creating…</>
            : <><CheckCircle size={14} weight="bold" /> {isFailed ? "Retry Create Receipt" : "Create Receipt"}</>}
        </button>
        <button onClick={isSubmitting ? undefined : onReviewEdit}
          disabled={isSubmitting}
          className={"flex items-center justify-center gap-1.5 px-3 py-3 text-xs font-bold transition-colors "
            + (isSubmitting ? "text-gray-400 cursor-not-allowed" : "text-[#0020C2] hover:bg-[#0020C2]/5")}>
          <PencilSimple size={14} weight="bold" /> Review / Edit
        </button>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Shared Chat Components
// ══════════════════════════════════════════════════════════════════════════════
function ProgressBar({ progress }) {
  if (!progress) return null;
  const pctMatch = progress.match(/\((\d+)%\)/);
  const stepMatch = progress.match(/(\d+)\/(\d+)/);
  if (!pctMatch || !stepMatch) return null;
  const pct = parseInt(pctMatch[1], 10);
  const [, done, total] = stepMatch;
  return (
    <div className="px-4 pt-3 pb-1">
      <div className="flex justify-between text-xs text-gray-500 mb-1">
        <span>Step {done} of {total}</span><span>{pct}% complete</span>
      </div>
      <div className="h-1.5 bg-gray-100 rounded-full overflow-hidden">
        <div className="h-full bg-[#0020C2] transition-all duration-300" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
}

// FIX 1: QuickActions component removed — no buttons shown above chat

function ChatMarkdown({ children }) {
  return (
    <div className="chat-md text-sm leading-relaxed">
      <ReactMarkdown remarkPlugins={[remarkGfm]}
        components={{
          p: ({ node, ...props }) => <p className="mb-2 last:mb-0 whitespace-pre-wrap" {...props} />,
          strong: ({ node, ...props }) => <strong className="font-bold text-gray-900" {...props} />,
          ul: ({ node, ...props }) => <ul className="list-disc pl-5 mb-2 space-y-0.5" {...props} />,
          ol: ({ node, ...props }) => <ol className="list-decimal pl-5 mb-2 space-y-0.5" {...props} />,
          li: ({ node, ...props }) => <li className="text-sm" {...props} />,
          h3: ({ node, ...props }) => <h3 className="text-sm font-bold mb-1 mt-2 text-[#0020C2]" {...props} />,
          code: ({ node, inline, ...props }) =>
            inline
              ? <code className="font-mono text-[12px] bg-gray-100 px-1 py-0.5 rounded" {...props} />
              : <code className="font-mono text-[12px] block bg-gray-50 border border-gray-200 rounded p-2 overflow-x-auto" {...props} />,
        }}>
        {children}
      </ReactMarkdown>
    </div>
  );
}

// FIX 3: ReceiptMethodPicker now accepts options prop from backend next_field_ui
// Falls back to RECEIPT_METHOD_FALLBACK only if no options provided
// RESTORED from old code: fallback list re-enabled
function ReceiptMethodPicker({ onSelect, options: propOptions }) {
  const [q, setQ] = React.useState("");

  // Use BU-scoped options from backend if available, else show fallback
  const baseOptions = (propOptions && propOptions.length > 0)
    ? propOptions.map((o) => ({ label: o.label || o.value, value: o.value || o.label, sub: o.sublabel || "" }))
    : RECEIPT_METHOD_FALLBACK.map((o) => ({ label: o, value: o, sub: "" }));

  const filtered = q
    ? baseOptions.filter((o) =>
        o.label.toLowerCase().includes(q.toLowerCase()) ||
        (o.sub || "").toLowerCase().includes(q.toLowerCase()))
    : baseOptions;

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white mx-4 mb-2">
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder="Search receipt method…"
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="max-h-44 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No options available right now.</div>
        )}
        {filtered.map((opt, idx) => (
          <div key={idx} onClick={() => onSelect(opt.value)}
            className="px-3 py-2 hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0">
            <div className="text-xs font-medium text-gray-800">{opt.label}</div>
            {opt.sub && <div className="text-[10px] text-gray-400 mt-0.5">{opt.sub}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}

function SearchableChatOptions({ fieldUi, onPick }) {
  const [q, setQ] = React.useState("");
  const options = fieldUi.options || [];
  const filtered = q ? options.filter((o) => o.label.toLowerCase().includes(q.toLowerCase())) : options;
  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white"
      data-testid={`chat-field-ui-${fieldUi.field}`}>
      <input autoFocus value={q} onChange={(e) => setQ(e.target.value)}
        placeholder={`Search ${(fieldUi.field || "").replace(/_/g, " ")}…`}
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100" />
      <div className="max-h-40 overflow-y-auto">
        {options.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No options available right now.</div>}
        {options.length > 0 && filtered.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No matches</div>}
        {filtered.slice(0, 50).map((opt) => (
          <div key={opt.value} onClick={() => onPick(opt)}
            className="px-3 py-1.5 text-xs hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0">
            <div className="font-medium text-gray-800">{opt.label}</div>
            {opt.sublabel && <div className="text-[10px] text-gray-400 mt-0.5">{opt.sublabel}</div>}
          </div>
        ))}
      </div>
    </div>
  );
}


// ─────────────────────────────────────────────────────────────────────────────
// InlineChatCustomerSearch
// Searchable dropdown rendered inside the chat bubble for customer selection.
// Calls GET /lov/customers?q=...&business_unit=... which is BU-scoped.
// ─────────────────────────────────────────────────────────────────────────────
function InlineChatCustomerSearch({ onSelect, options: propOptions }) {
  // Options are preloaded by the backend (_customer_options) and passed via
  // next_field_ui.options — same pattern as ReceiptMethodPicker.
  // Each option: { label: CustomerName, value: AccountNumber, sublabel: "Acct: ...", party: PartyNumber }
  const [q, setQ] = React.useState("");

  const baseOptions = (propOptions && propOptions.length > 0) ? propOptions : [];

  const filtered = q
    ? baseOptions.filter((o) =>
        (o.label || "").toLowerCase().includes(q.toLowerCase()) ||
        (o.sublabel || "").toLowerCase().includes(q.toLowerCase()) ||
        (o.value || "").toLowerCase().includes(q.toLowerCase()))
    : baseOptions;

  const handlePick = (opt) => {
    // opt.label = customer name only (no account number suffix)
    // opt.value = AccountNumber → CustomerAccountNumber in Oracle POST
    // opt.site  = SiteName from LOV → CustomerSite in Oracle POST
    onSelect({
      customer_name:           opt.label || opt.value,   // clean name only
      customer_account_number: opt.value,
      customer_party_number:   opt.party || "",
      customer_site:           opt.site  || "",          // SiteName from LOV
      customer_site_number:    "",
    });
  };

  if (baseOptions.length === 0) {
    return (
      <div className="mt-2 border border-[#0020C2]/20 rounded-lg bg-white mx-4 mb-2 px-3 py-3">
        <div className="text-xs text-gray-400 text-center">
          No customers found. You can type the account number in the chat.
        </div>
      </div>
    );
  }

  return (
    <div className="mt-2 border border-[#0020C2]/20 rounded-lg overflow-hidden bg-white mx-4 mb-2">
      <input
        autoFocus
        value={q}
        onChange={(e) => setQ(e.target.value)}
        placeholder="Search customer name or account…"
        className="w-full px-3 py-2 text-xs outline-none border-b border-gray-100"
      />
      <div className="max-h-52 overflow-y-auto">
        {filtered.length === 0 && (
          <div className="px-3 py-2 text-xs text-gray-400">No matches</div>
        )}
        {filtered.map((opt, idx) => (
          <div
            key={idx}
            onClick={() => handlePick(opt)}
            className="px-3 py-2 hover:bg-[#0020C2]/5 cursor-pointer border-b border-gray-50 last:border-0"
          >
            <div className="text-xs font-medium text-gray-800">{opt.label}</div>
            <div className="text-[10px] text-gray-400 mt-0.5">{opt.sublabel}</div>
          </div>
        ))}
      </div>
      {baseOptions.length > 0 && (
        <div className="px-3 py-1.5 border-t border-gray-100 text-[10px] text-gray-400">
          {baseOptions.length} customer{baseOptions.length !== 1 ? "s" : ""}
        </div>
      )}
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// PDF Upload for Receipt Number (shown inside chat bubble when backend requests it)
// NEW FEATURE — only addition from new code that is kept
// ══════════════════════════════════════════════════════════════════════════════
function ReceiptNumberPdfUpload({ sessionId, onUploaded, onValidationFail }) {
  const [status, setStatus] = React.useState("idle");
  const [fileName, setFileName] = React.useState(null);
  const [errorMsg, setErrorMsg] = React.useState(null);
  const [dragOver, setDragOver] = React.useState(false);
  const fileInputRef = React.useRef(null);

  const upload = async (file) => {
    if (!file) return;
    setFileName(file.name);
    setStatus("uploading");
    setErrorMsg(null);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(
        `/erp-agents/receipt-create/${sessionId}/upload-receipt-number-pdf`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      if (data?.is_receipt === false) {
        setStatus("invalid");
        const reason = data?.validation_reason ||
          `⚠️ **"${file.name}"** does not appear to be a receipt document.\n\nPlease upload a Bank Credit Advice, Remittance Advice, or Payment Receipt.`;
        setErrorMsg(reason);
        onValidationFail?.(reason);
        return;
      }
      if (data?.is_receipt && !data?.receipt_number) {
        setStatus("invalid");
        const reason = `⚠️ Could not extract a Receipt Number from **"${file.name}"**.\n\nPlease upload a clearer document or try another file.`;
        setErrorMsg(reason);
        onValidationFail?.(reason);
        return;
      }
      setStatus("success");
      onUploaded?.(data);
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      setStatus("error");
      setErrorMsg(msg);
    }
  };

  const handleFileChange = (e) => { const f = e.target.files?.[0]; if (f) upload(f); e.target.value = ""; };
  const handleDrop = (e) => { e.preventDefault(); setDragOver(false); const f = e.dataTransfer.files?.[0]; if (f) upload(f); };
  const reset = () => { setStatus("idle"); setFileName(null); setErrorMsg(null); };

  if (status === "success") return (
    <div className="border border-green-200 bg-green-50 rounded-lg p-3 flex items-center gap-3 mt-2">
      <CheckCircle size={18} weight="fill" className="text-green-600 shrink-0" />
      <div className="min-w-0">
        <p className="text-xs font-bold text-green-800">Receipt PDF uploaded — extracting number…</p>
        <p className="text-[11px] text-green-600 truncate">{fileName}</p>
      </div>
    </div>
  );
  if (status === "invalid" || status === "error") return (
    <div className="border border-amber-200 bg-amber-50 rounded-lg p-3 space-y-2 mt-2">
      <div className="flex items-start gap-2">
        <WarningCircle size={15} weight="fill" className="text-amber-600 shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-bold text-amber-800 mb-1">
            {status === "invalid" ? "Invalid document" : "Upload failed"}
          </p>
          <p className="text-[11px] text-amber-700 whitespace-pre-line">{errorMsg || "Please try again."}</p>
        </div>
      </div>
      <button onClick={reset} className="text-[11px] font-semibold text-amber-700 hover:text-amber-900 flex items-center gap-1">
        <ArrowClockwise size={12} weight="bold" /> Try another file
      </button>
    </div>
  );
  if (status === "uploading") return (
    <div className="border border-[#0020C2]/20 bg-blue-50/60 rounded-lg p-3 flex items-center gap-3 mt-2">
      <span className="w-4 h-4 border-2 border-[#0020C2]/30 border-t-[#0020C2] rounded-full animate-spin shrink-0" />
      <div>
        <p className="text-xs font-bold text-[#0020C2]">Reading receipt PDF…</p>
        <p className="text-[11px] text-gray-500 truncate">{fileName}</p>
        <p className="text-[10px] text-gray-400 mt-0.5">Extracting Receipt Number only</p>
      </div>
    </div>
  );
  return (
    <div
      className={`border-2 border-dashed rounded-lg p-3 mt-2 transition-colors cursor-pointer
        ${dragOver ? "border-[#0020C2] bg-[#0020C2]/5" : "border-gray-200 hover:border-[#0020C2]/50 hover:bg-gray-50/60"}`}
      onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
      onDragLeave={() => setDragOver(false)}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
    >
      <input ref={fileInputRef} type="file" accept=".pdf,.png,.jpg,.jpeg"
        className="hidden" onChange={handleFileChange} />
      <div className="flex items-center gap-3">
        <div className="w-8 h-8 rounded-full bg-[#0020C2]/10 flex items-center justify-center shrink-0">
          <FilePdf size={16} weight="bold" className="text-[#0020C2]" />
        </div>
        <div>
          <p className="text-xs font-bold text-gray-800">Upload Receipt PDF</p>
          <p className="text-[10px] text-gray-500">Only the Receipt Number will be extracted · PDF, PNG, JPEG</p>
        </div>
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// Chat bubble — updated to support pdf_upload_receipt_number uiType (new feature)
// All other behaviour identical to old code
// ══════════════════════════════════════════════════════════════════════════════
function ReceiptChatBubble({ msg, onOptionClick, businessUnit = "", sessionId, onPdfUploaded, onPdfValidationFail }) {
  const isUser = msg.role === "user";
  const fieldUi = !isUser && !msg.loading
    ? (msg.next_field_ui || msg.result?.next_field_ui)
    : null;
  return (
    <div className={`flex gap-2.5 ${isUser ? "flex-row-reverse" : "flex-row"}`}>
      <div className={`shrink-0 w-6 h-6 flex items-center justify-center rounded-full text-white ${isUser ? "bg-[#0020C2]" : "bg-gray-700"}`}>
        {isUser ? <UserIcon size={11} weight="bold" /> : <Robot size={11} weight="bold" />}
      </div>
      <div className={`max-w-[82%] rounded-2xl px-3.5 py-2 text-sm leading-relaxed
        ${isUser ? "bg-[#0020C2] text-white rounded-tr-sm" : "bg-white border border-gray-200 text-gray-900 rounded-tl-sm shadow-sm"}`}>
        {msg.loading && (
          <div className="flex items-center gap-1 py-0.5">
            {[0, 150, 300].map((d) => (
              <span key={d} className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: `${d}ms` }} />
            ))}
          </div>
        )}
        {!msg.loading && msg.text && (
          isUser ? <p className="whitespace-pre-wrap">{msg.text}</p> : <ChatMarkdown>{msg.text}</ChatMarkdown>
        )}
        {(fieldUi?.uiType === "customer_search" || fieldUi?.uiType === "customer_picker") && (
          <InlineChatCustomerSearch
            options={fieldUi?.options || []}
            onSelect={(data) => onOptionClick?.({ value: "__customer_selected__", customerData: data })}
          />
        )}
        {fieldUi?.uiType === "searchable_dropdown" && (
          <SearchableChatOptions fieldUi={fieldUi} onPick={(opt) => onOptionClick?.(opt)} />
        )}
        {fieldUi?.uiType === "radio" && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {(fieldUi.options || []).map((opt) => (
              <button key={opt.value} onClick={() => onOptionClick?.(opt)}
                className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors">
                {opt.label}
              </button>
            ))}
          </div>
        )}
        {/* NEW FEATURE: inline PDF upload for receipt number extraction */}
        {fieldUi?.uiType === "pdf_upload_receipt_number" && sessionId && (
          <ReceiptNumberPdfUpload
            sessionId={sessionId}
            onUploaded={(data) => onPdfUploaded?.(data)}
            onValidationFail={(reason) => onPdfValidationFail?.(reason)}
          />
        )}
        {fieldUi && !["searchable_dropdown", "radio", "customer_picker", "customer_search", "attachment_upload", "pdf_upload_receipt_number"].includes(fieldUi.uiType) && fieldUi.options?.length > 0 && (
          <div className="mt-2 flex flex-wrap gap-1.5">
            {fieldUi.options.map((opt) => (
              <button key={opt.value} onClick={() => onOptionClick?.(opt)}
                className="text-xs px-3 py-1.5 border border-[#0020C2]/30 rounded-full text-[#0020C2] bg-[#0020C2]/5 hover:bg-[#0020C2]/10 transition-colors text-left">
                {opt.label}
              </button>
            ))}
          </div>
        )}
        {msg.timestamp && (
          <div className={`text-[9px] mt-1 opacity-40 ${isUser ? "text-right" : "text-left"}`}>{msg.timestamp}</div>
        )}
      </div>
    </div>
  );
}

function ReceiptInputBar({ inputRef, value, onChange, onKeyDown, onSend, busy, onAttach, attaching, attachedCount, disableInput }) {
  const fileInputRef = React.useRef(null);
  return (
    <div className="border-t border-gray-200 px-4 py-3 bg-white shrink-0">
      <div className="flex items-end gap-2 border border-gray-300 bg-gray-50 focus-within:border-[#0020C2] focus-within:ring-2 focus-within:ring-blue-100">
        {onAttach && (
          <>
            <input ref={fileInputRef} type="file" accept=".pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg"
              multiple className="hidden"
              onChange={(e) => { const files = Array.from(e.target.files || []); if (files.length) onAttach(files); e.target.value = ""; }} />
            <button onClick={() => fileInputRef.current?.click()} disabled={attaching}
              title="Attach receipt document"
              className="m-2 w-8 h-8 flex items-center justify-center text-gray-500 hover:text-[#0020C2] hover:bg-gray-100 rounded transition-colors shrink-0 disabled:opacity-40 relative">
              {attaching
                ? <span className="w-3 h-3 border-2 border-gray-300 border-t-[#0020C2] rounded-full animate-spin" />
                : <Paperclip size={16} weight="bold" />}
              {attachedCount > 0 && (
                <span className="absolute -top-1 -right-1 w-4 h-4 bg-[#0020C2] text-white text-[9px] rounded-full flex items-center justify-center">
                  {attachedCount}
                </span>
              )}
            </button>
          </>
        )}
        <textarea ref={inputRef} value={value} onChange={onChange} onKeyDown={onKeyDown}
          rows={1} disabled={busy || disableInput}
          placeholder={disableInput ? "Select an option above to continue" : "Continue the conversation…"}
          className="flex-1 bg-transparent px-3 py-2.5 text-sm resize-none outline-none placeholder-gray-400 disabled:opacity-50 leading-relaxed"
          style={{ minHeight: "2.5rem", maxHeight: "7.5rem" }} />
        <button onClick={onSend} disabled={busy || !value.trim() || disableInput}
          className="m-2 w-8 h-8 bg-[#0020C2] text-white flex items-center justify-center hover:bg-[#001AA3] disabled:opacity-40 transition-colors shrink-0">
          {busy ? <span className="w-3 h-3 border-2 border-white/30 border-t-white rounded-full animate-spin" /> : <PaperPlaneRight size={14} weight="fill" />}
        </button>
      </div>
      <span className="text-[10px] text-gray-400 mt-1 block">↵ send · shift+↵ new line</span>
    </div>
  );
}

function ChatHistoryControl({ agentKey, currentSessionId, onNewChat, onOpenSession }) {
  const [open, setOpen] = React.useState(false);
  const [sessions, setSessions] = React.useState([]);
  const [loading, setLoading] = React.useState(false);
  const wrapRef = React.useRef(null);
  const load = React.useCallback(async () => {
    if (!agentKey) return;
    setLoading(true);
    try { const { data } = await api.get(`/erp-agents/${agentKey}/sessions`); setSessions(Array.isArray(data) ? data : []); }
    catch { setSessions([]); }
    finally { setLoading(false); }
  }, [agentKey]);
  React.useEffect(() => { if (open) load(); }, [open, load]);
  React.useEffect(() => {
    if (!open) return;
    const onDocClick = (e) => { if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onDocClick);
    return () => document.removeEventListener("mousedown", onDocClick);
  }, [open]);
  const deleteSession = async (sid, e) => {
    e.stopPropagation();
    if (!window.confirm("Delete this conversation?")) return;
    try { await api.delete(`/erp-agents/${agentKey}/sessions/${sid}`); load(); } catch { }
  };
  return (
    <div className="flex items-center gap-3" ref={wrapRef}>
      <button onClick={onNewChat}
        className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1 shrink-0">
        <Plus size={13} weight="bold" /> New Chat
      </button>
      <div className="relative">
        <button onClick={() => setOpen((v) => !v)}
          className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
          History <CaretDown size={10} weight="bold" className={`transition-transform ${open ? "rotate-180" : ""}`} />
        </button>
        {open && (
          <div className="absolute right-0 z-40 mt-2 w-72 bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-y-auto">
            {loading && <div className="px-3 py-2 text-xs text-gray-400">Loading…</div>}
            {!loading && sessions.length === 0 && <div className="px-3 py-2 text-xs text-gray-400">No previous conversations.</div>}
            {sessions.map((s) => (
              <div key={s.session_id}
                onClick={() => { onOpenSession(s.session_id); setOpen(false); }}
                className={`px-3 py-2 text-xs cursor-pointer border-b border-gray-50 last:border-0 hover:bg-[#0020C2]/5 flex items-center gap-2 ${s.session_id === currentSessionId ? "bg-[#0020C2]/5" : ""}`}>
                <div className="min-w-0 flex-1">
                  <div className="font-semibold text-gray-800 truncate">{s.title}</div>
                  <div className="text-[10px] text-gray-400">
                    {s.updated_at ? new Date(s.updated_at).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : ""}
                  </div>
                </div>
                <button onClick={(e) => deleteSession(s.session_id, e)} className="text-[#DC2626] shrink-0 p-1">
                  <Trash size={11} weight="bold" />
                </button>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

// ══════════════════════════════════════════════════════════════════════════════
// MAIN MODAL
// ══════════════════════════════════════════════════════════════════════════════
const FIELD_LABELS_MAP = {
  business_unit:                 "Business Unit",
  receipt_method:                "Receipt Method",
  customer_reference_number:     "Customer Reference Number",
  currency:                      "Currency",
  entered_amount:                "Amount",
  receipt_date:                  "Receipt Date",
  accounting_date:               "Accounting Date",
  remittance_bank_name:          "Remittance Bank Name",
  remittance_bank_branch:        "Remittance Bank Branch",
  remittance_bank_account:       "Remittance Bank Account",
  remittance_bank_deposit_date:  "Remittance Bank Deposit Date",
  receipt_number:                "Receipt Number",
};

export default function ARReceiptAgentModal({ onClose }) {
  const { openAgent, setOpenAgent, setSetupOpen, profile, agents } = useErp();
  const agent = agents.find((a) => a.key === openAgent);
  const sessionKey = "receipt_session_id";

  // Always generate a fresh session ID on modal open.
  // Never reuse a stored session — stale wizard fields from the previous run
  // would appear on the right panel before the user even types anything.
  const [sessionId, setSessionId] = React.useState(() => {
    const fresh = crypto.randomUUID();
    sessionStorage.setItem(sessionKey, fresh);
    return fresh;
  });
  React.useEffect(() => { window.__receiptSessionId = sessionId; return () => { delete window.__receiptSessionId; }; }, [sessionId]);

  // FIX 3: store BU-scoped receipt method options from backend next_field_ui
  const [receiptMethodOptions, setReceiptMethodOptions] = React.useState([]);

  // /start is called lazily inside sendMessage on the first user message,
  // NOT on mount — nothing renders before the user types.

  const [messages, setMessages] = React.useState([]);
  const [input, setInput] = React.useState("");
  const [busy, setBusy] = React.useState(false);
  const [chatStarted, setChatStarted] = React.useState(false);
  const [copilotProgress, setCopilotProgress] = React.useState(null);
  const [attaching, setAttaching] = React.useState(false);
  const [attachedCount, setAttachedCount] = React.useState(0);
  const [fieldValues, setFieldValues] = React.useState({});
  const [missingFields, setMissingFields] = React.useState([]);
  const fieldValuesRef = React.useRef({});
  React.useEffect(() => { fieldValuesRef.current = fieldValues; }, [fieldValues]);
  const [extractedData, setExtractedData] = React.useState(null);
  const [uiMode, setUiMode] = React.useState("chat");
  const [receiptStatus, setReceiptStatus] = React.useState("draft");
  const [oracleResult, setOracleResult] = React.useState(null);
  const [customerData, setCustomerData] = React.useState({
    customer_name: "", customer_account_number: "", customer_party_number: "", customer_site: ""
  });

  const messagesEndRef = React.useRef(null);
  const inputRef = React.useRef(null);
  React.useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [messages]);
  React.useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, []);

  const handleAttachFiles = async (files) => {
    const file = files[0];
    if (!file) return;
    setAttaching(true);
    setMessages((prev) => [...prev, { id: Date.now() + 1, role: "assistant", loading: true }]);
    try {
      const formData = new FormData();
      formData.append("file", file);
      const { data } = await api.post(
        `/erp-agents/receipt-create/${sessionId}/upload-receipt-attachment`,
        formData,
        { headers: { "Content-Type": "multipart/form-data" } }
      );
      const isReceipt = data?.is_receipt !== false;
      const extraction = data?.extraction || data?.fields_collected || {};
      const hasReceiptFields = extraction.receipt_number || extraction.receipt_amount ||
        extraction.amount || extraction.remitter || extraction.remittance_amount;
      if (!isReceipt || (!hasReceiptFields && data?.is_receipt === false)) {
        const reason = data?.validation_reason ||
          `⚠️ **"${file.name}"** doesn't appear to be a receipt document.\n\nAccepted types: ${ACCEPTED_DOC_TYPES.join(", ")}.\n\nPlease upload a valid receipt document.`;
        setMessages((prev) => prev.map((m) => m.loading ? { ...m, loading: false, timestamp: ts(), text: reason } : m));
        setAttaching(false);
        return;
      }
      setAttachedCount(1);
      const backendFields = data?.fields_collected || {};
      const allExtracted = { ...extraction, ...backendFields };
      setExtractedData(allExtracted);
      mergeIntoWizard(allExtracted);
      if (data?.progress) setCopilotProgress(data.progress);
      const extractionSync = {};
      const EXTRACT_MAP = {
        receipt_number: "receipt_number", receipt_date: "receipt_date",
        entered_amount: "entered_amount", amount: "entered_amount",
        currency: "currency", receipt_method: "receipt_method",
        accounting_date: "accounting_date",
        remittance_bank_name: "remittance_bank_name",
        remittance_bank_branch: "remittance_bank_branch",
        remittance_bank_account: "remittance_bank_account",
        remittance_bank_deposit_date: "remittance_bank_deposit_date",
        customer_reference_number: "customer_reference_number",
      };
      for (const [extractKey, stateKey] of Object.entries(EXTRACT_MAP)) {
        const val = allExtracted[extractKey];
        if (!isMissingValue(val) && !String(val).toUpperCase().includes("ATTACHMENT_UPLOADED")) {
          extractionSync[stateKey] = normalizeFieldValue(stateKey, String(val));
        }
      }
      if (Object.keys(extractionSync).length > 0) {
        api.post("/erp-agents/receipt-create/set-fields", {
          session_id: sessionId, fields: extractionSync,
        }).catch((e) => console.warn("[receipt] extraction sync failed:", e?.message));
      }
      const reply = data?.reply || data?.response || `✅ **Document processed:** ${file.name}\n\nReview the extraction summary below.`;
      setMessages((prev) => prev.map((m) => m.loading ? { ...m, loading: false, timestamp: ts(), text: reply, result: data } : m));
      setUiMode("extraction");
    } catch (err) {
      const msg = err?.response?.data?.detail || err.message || "Upload failed";
      setMessages((prev) => prev.map((m) => m.loading ? { ...m, loading: false, timestamp: ts(), text: `❌ ${msg}` } : m));
    } finally {
      setAttaching(false);
    }
  };

  const mergeIntoWizard = React.useCallback((raw) => {
    setFieldValues((prev) => {
      const merged = { ...prev };
      for (const [chatKey, val] of Object.entries(raw)) {
        if (isMissingValue(val)) continue;
        if (isNavCommand(String(val))) continue;
        const wizardKey = CHAT_TO_WIZARD[chatKey] || chatKey;
        const valStr = String(val);
        if (valStr.toUpperCase().includes("ATTACHMENT_UPLOADED")) continue;
        if ((wizardKey === "remittance_bank_account" || wizardKey === "remittance_bank_account_number") &&
            /[A-Za-z]/.test(valStr)) continue;
        if (valStr.startsWith("__") && valStr.endsWith("__")) continue;
        const normalized = normalizeFieldValue(wizardKey, valStr);
        merged[wizardKey] = normalized;
        merged[`${wizardKey}__label`] = valStr;
        if (wizardKey === "entered_amount") { merged["receipt_amount"] = normalized; merged["receipt_amount__label"] = valStr; }
        if (wizardKey === "remittance_bank_account") { merged["remittance_bank_account_number"] = normalized; }
      }
      return merged;
    });
  }, []);

  const [missingQueue, setMissingQueue] = React.useState([]);
  const [currentMissingField, setCurrentMissingField] = React.useState(null);
  React.useEffect(() => { messagesEndRef.current?.scrollIntoView({ behavior: "smooth" }); }, [currentMissingField, uiMode]);

  const askNextMissingField = React.useCallback((queue) => {
    const next = queue[0];
    if (!next) {
      setCurrentMissingField(null);
      setMissingQueue([]);
      setMissingFields([]);
      setUiMode("summary");
      setMessages((prev) => [...prev, {
        id: Date.now(), role: "assistant", timestamp: ts(),
        text: "✅ All fields collected! Click **Create Receipt** to proceed.",
      }]);
      return;
    }
    setCurrentMissingField(next);
    setMissingQueue(queue.slice(1));
    const label = FIELD_LABELS_MAP[next] || next.replace(/_/g, " ");
    let hint = "";
    let fieldUiPayload = undefined;
    if (next === "receipt_method") {
      hint = "\n\nPlease select from the options below:";
      // FIX 3: use BU-scoped options stored from backend response
      const opts = receiptMethodOptions.length > 0
        ? receiptMethodOptions
        : RECEIPT_METHOD_FALLBACK.map((o) => ({ label: o, value: o }));
      fieldUiPayload = { field: next, uiType: "searchable_dropdown", options: opts };
    } else if (next === "accounting_date" || next === "receipt_date" || next.includes("date")) {
      hint = " (DD/MM/YYYY or YYYY-MM-DD)";
    } else if (next === "entered_amount") {
      hint = " (numbers only, e.g. 250000.00)";
    } else if (next === "currency") {
      hint = " (ISO code, e.g. USD, AED, EUR)";
    }
    setMessages((prev) => [...prev, {
      id: Date.now(), role: "assistant", timestamp: ts(),
      text: `Please provide the **${label}**${hint}:`,
      next_field_ui: fieldUiPayload,
      result: fieldUiPayload ? { next_field_ui: fieldUiPayload } : undefined,
    }]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [receiptMethodOptions]);

  const handlePopulateFields = (data) => {
    const extracted = data || extractedData;
    if (!extracted) return;
    mergeIntoWizard(extracted);
    const syncPayload = {};
    const EXTRACT_STATE_MAP = {
      receipt_number: "receipt_number", receipt_date: "receipt_date",
      entered_amount: "entered_amount", amount: "entered_amount",
      currency: "currency", receipt_method: "receipt_method",
      accounting_date: "accounting_date",
      remittance_bank_name: "remittance_bank_name",
      remittance_bank_branch: "remittance_bank_branch",
      remittance_bank_account: "remittance_bank_account",
      remittance_bank_deposit_date: "remittance_bank_deposit_date",
      customer_reference_number: "customer_reference_number",
    };
    for (const [extractKey, stateKey] of Object.entries(EXTRACT_STATE_MAP)) {
      const val = extracted[extractKey];
      if (!isMissingValue(val) && !String(val).toUpperCase().includes("ATTACHMENT_UPLOADED")) {
        syncPayload[stateKey] = normalizeFieldValue(stateKey, String(val));
      }
    }
    if (Object.keys(syncPayload).length > 0) {
      api.post("/erp-agents/receipt-create/set-fields", {
        session_id: sessionId, fields: syncPayload,
      }).catch((e) => console.warn("[receipt] populate sync failed:", e?.message));
    }
    const updatedValues = { ...fieldValues };
    for (const [chatKey, val] of Object.entries(extracted)) {
      if (!val) continue;
      const wizardKey = CHAT_TO_WIZARD[chatKey] || chatKey;
      updatedValues[wizardKey] = String(val);
    }
    const missing = REQUIRED_RECEIPT_FIELDS.filter((f) => isMissingValue(updatedValues[f]));
    setMissingFields(missing);
    setUiMode("summary");
    if (missing.length > 0) {
      const firstMissing = missing[0];
      setCurrentMissingField(firstMissing);
      setMissingQueue(missing.slice(1));
      const label = FIELD_LABELS_MAP[firstMissing] || firstMissing.replace(/_/g, " ");
      setMessages((prev) => [...prev, {
        id: Date.now(), role: "assistant", timestamp: ts(),
        text: `✅ Fields populated from document.\n\nReview the summary below. Some fields are still missing — please provide the **${label}**:`,
      }]);
    } else {
      setMessages((prev) => [...prev, {
        id: Date.now(), role: "assistant", timestamp: ts(),
        text: "✅ All fields populated! Review the summary below and click **Create Receipt** to proceed.",
      }]);
    }
  };

  const handleConfirm = async () => {
    const currentFieldValues = fieldValuesRef.current;
    if (!chatStarted) setChatStarted(true);
    setReceiptStatus("submitting");
    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", text: "CONFIRM", timestamp: ts() },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);
    try {
      const SYNC_FIELDS = [
        "receipt_method", "accounting_date", "receipt_date", "entered_amount",
        "currency", "customer_reference_number",
        "customer_name", "customer_account_number", "customer_party_number",
        "customer_site", "customer_site_number",
        "remittance_bank_name", "remittance_bank_branch", "remittance_bank_account",
        "remittance_bank_deposit_date", "receipt_number",
      ];
      const syncPayload = {};
      for (const f of SYNC_FIELDS) {
        const val = currentFieldValues[f];
        if (!isMissingValue(val)) syncPayload[f] = val;
      }
      if (Object.keys(syncPayload).length > 0) {
        try {
          await api.post("/erp-agents/receipt-create/set-fields", {
            session_id: sessionId, fields: syncPayload,
          });
        } catch (err) { console.warn("[receipt] pre-confirm sync:", err?.message); }
      }
      let data = null;
      for (let attempt = 0; attempt < 2; attempt++) {
        const res = await api.post("/erp-agents/receipt-create/chat", {
          session_id: sessionId, message: "CONFIRM",
        });
        data = res.data;
        if (data?.receipt_created || data?.oracle_response?.success) break;
        const reply = (data?.reply || "").toLowerCase();
        if (reply.includes("failed") || reply.includes("error")) break;
      }
      if (data?.progress) setCopilotProgress(data.progress);
      if (data?.fields_collected) mergeIntoWizard(data.fields_collected);
      let replyText;
      if (data?.receipt_created || data?.oracle_response?.success) {
        const receiptId = data?.oracle_response?.receipt_number
          || data?.oracle_response?.receipt_id
          || currentFieldValues.receipt_number || "";
        replyText = "✅ **Receipt Created Successfully!**"
          + (receiptId ? "\n\nReceipt Number: **" + receiptId + "**" : "")
          + "\n\nThe AR Standard Receipt has been created in Oracle Fusion.";
        setReceiptStatus("success");
        setOracleResult(data?.oracle_response || {});
        setUiMode("chat");
      } else {
        const rawReply = data?.reply || data?.response || data?.message || "Unknown error";
        replyText = "❌ **" + rawReply + "**";
        setReceiptStatus("failed");
        setUiMode("summary");
        if (data?.fields_missing?.length > 0) {
          const stillMissing = data.fields_missing.filter((f) => isMissingValue(currentFieldValues[f]));
          if (stillMissing.length > 0) {
            setMissingFields(stillMissing);
            setMissingQueue(stillMissing.slice(1));
            setCurrentMissingField(stillMissing[0]);
            setUiMode("chat");
          }
        }
      }
      setMessages((prev) => prev.map((m) =>
        m.loading ? { ...m, loading: false, text: replyText, result: data, timestamp: ts() } : m
      ));
    } catch (e) {
      setReceiptStatus("failed");
      const errMsg = e?.response?.data?.detail || e.message || "Request failed";
      setMessages((prev) => prev.map((m) =>
        m.loading ? { ...m, loading: false, timestamp: ts(), text: "❌ **Error:** " + errMsg } : m
      ));
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const sendMessage = async (text) => {
    const userText = (text || "").trim();
    if (!userText || busy) return;
    if (userText.toUpperCase() === "CONFIRM") {
      setInput("");
      if (inputRef.current) inputRef.current.style.height = "auto";
      handleConfirm();
      return;
    }
    if (isNavCommand(userText)) {
      const lower = userText.toLowerCase();
      if (lower.includes("review") || lower.includes("edit")) { setUiMode("review"); return; }
      if (lower.includes("populate")) { handlePopulateFields(extractedData); return; }
      if (lower === "cancel" || lower === "back") { setUiMode("chat"); return; }
      if (lower === "done" || lower.includes("done_editing") || lower === "done editing") {
        setUiMode(extractedData ? "extraction" : "chat"); return;
      }
    }
    if (currentMissingField && userText.toUpperCase() !== "CONFIRM") {
      setInput("");
      if (inputRef.current) inputRef.current.style.height = "auto";
      const fieldKey = currentMissingField;
      const normalized = normalizeFieldValue(fieldKey, userText);
      setFieldValues((prev) => ({ ...prev, [fieldKey]: normalized, [`${fieldKey}__label`]: userText }));
      setMessages((prev) => [...prev, { id: Date.now(), role: "user", text: userText, timestamp: ts() }]);
      setCurrentMissingField(null);
      api.post("/erp-agents/receipt-create/set-fields", {
        session_id: sessionId, fields: { [fieldKey]: normalized },
      })
        .then((res) => { if (res.data?.fields_collected) mergeIntoWizard(res.data.fields_collected); })
        .catch((err) => console.warn("[receipt] set-fields:", err?.message));
      askNextMissingField(missingQueue);
      return;
    }
    setInput("");
    if (inputRef.current) inputRef.current.style.height = "auto";

    // On the very first user message: call /start to get a fresh session +
    // greeting + receipt_type options, show them in chat, then stop.
    // The user must pick a receipt type before the normal chat flow begins.
    if (!chatStarted) {
      setMessages([
        { id: Date.now(), role: "user", text: userText, timestamp: ts() },
        { id: Date.now() + 1, role: "assistant", loading: true },
      ]);
      setChatStarted(true);
      setBusy(true);
      try {
        const { data } = await api.post("/erp-agents/receipt-create/start");
        if (data?.session_id) {
          sessionStorage.setItem(sessionKey, data.session_id);
          setSessionId(data.session_id);
        }
        const greeting = data?.reply || data?.response ||
          "Hello! Let's get started with creating your AR Standard Receipt. Please select the **Receipt Type** to continue.";
        const fieldUi = data?.next_field_ui || {
          field: "receipt_type", uiType: "searchable_dropdown",
          options: [{ label: "Standard", value: "Standard" }, { label: "Miscellaneous", value: "Miscellaneous" }],
        };
        setMessages((prev) => prev.map((m) =>
          m.loading ? { ...m, loading: false, text: greeting, next_field_ui: fieldUi, result: { next_field_ui: fieldUi }, timestamp: ts() } : m
        ));
      } catch (e) {
        const fallbackUi = {
          field: "receipt_type", uiType: "searchable_dropdown",
          options: [{ label: "Standard", value: "Standard" }, { label: "Miscellaneous", value: "Miscellaneous" }],
        };
        setMessages((prev) => prev.map((m) =>
          m.loading ? { ...m, loading: false,
            text: "Hello! Let's get started with creating your AR Standard Receipt. Please select the **Receipt Type** to continue.",
            next_field_ui: fallbackUi, result: { next_field_ui: fallbackUi }, timestamp: ts() } : m
        ));
        console.warn("[receipt] /start failed:", e?.message);
      } finally {
        setBusy(false);
        setTimeout(() => inputRef.current?.focus(), 100);
      }
      return;
    }

    setMessages((prev) => [
      ...prev,
      { id: Date.now(), role: "user", text: userText, timestamp: ts() },
      { id: Date.now() + 1, role: "assistant", loading: true },
    ]);
    setBusy(true);
    try {
      const res = await api.post("/erp-agents/receipt-create/chat", {
        session_id: sessionId, message: userText,
      });
      const data = res.data;
      if (data?.progress) setCopilotProgress(data.progress);
      if (data?.fields_collected) mergeIntoWizard(data.fields_collected);

      // FIX 3: capture receipt method options when backend returns them
      const nextUi = data?.next_field_ui || data?.result?.next_field_ui;
      if (nextUi?.field === "receipt_method" && nextUi?.options?.length > 0) {
        setReceiptMethodOptions(nextUi.options);
      }

      const reply = data?.reply || data?.response || data?.message || "Done.";
      setMessages((prev) => prev.map((m) => m.loading ? { ...m, loading: false, text: reply, result: data, timestamp: ts() } : m));
    } catch (e) {
      setMessages((prev) => prev.map((m) => m.loading
        ? { ...m, loading: false, timestamp: ts(), text: `Error: ${e?.response?.data?.detail || e.message}` } : m));
    } finally {
      setBusy(false);
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
    e.target.style.height = "auto";
    e.target.style.height = Math.min(e.target.scrollHeight, 120) + "px";
  };
  const handleKeyDown = (e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendMessage(input); } };

  const startNewChat = async () => {
    // Reset all local state first
    setMessages([]); setInput(""); setChatStarted(false);
    setCopilotProgress(null); setAttachedCount(0);
    setFieldValues({}); setMissingFields([]);
    setExtractedData(null); setUiMode("chat");
    setCurrentMissingField(null); setMissingQueue([]);
    setReceiptStatus("draft"); setOracleResult(null);
    setReceiptMethodOptions([]);

    // Generate a fresh session ID — /start is called lazily on the next user message.
    const newId = crypto.randomUUID();
    sessionStorage.setItem(sessionKey, newId);
    setSessionId(newId);
    setTimeout(() => inputRef.current?.focus(), 100);
  };

  const openSession = async (sid) => {
    try {
      const { data } = await api.get(`/erp-agents/receipt-create/sessions/${sid}/messages`);
      const loaded = (data?.messages || []).map((m, i) => ({
        id: Date.now() + i, role: m.role, text: m.content,
        timestamp: m.created_at ? new Date(m.created_at).toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" }) : ts(),
        result: m.next_field_ui ? { next_field_ui: m.next_field_ui } : undefined,
      }));
      sessionStorage.setItem(sessionKey, sid);
      setSessionId(sid); setMessages(loaded); setChatStarted(true); setUiMode("chat");
    } catch (e) { window.alert(`Could not open conversation: ${e?.response?.data?.detail || e.message}`); }
  };

  const close = () => { setOpenAgent(null); onClose?.(); };

  return (
    <>
      <style>{`
        @keyframes chatFadeIn    { from { opacity:0; transform:translateX(-10px); } to { opacity:1; transform:translateX(0); } }
        @keyframes wizardSlideIn { from { opacity:0; transform:translateX(20px);  } to { opacity:1; transform:translateX(0); } }
        .receipt-modal-pre  { width:672px; max-width:calc(100vw - 2rem); max-height:90vh; }
        .receipt-modal-chat { width:min(92vw,1400px); height:90vh; }
      `}</style>
      <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4">
        <div onClick={(e) => e.stopPropagation()}
          className={`bg-white text-gray-900 border border-gray-300 flex flex-col overflow-hidden
            ${chatStarted ? "receipt-modal-chat" : "receipt-modal-pre"}`}
          style={{ transition: "width 0.45s cubic-bezier(0.4,0,0.2,1), height 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
          {/* Header */}
          <div className="px-5 py-4 border-b border-gray-200 flex items-start justify-between shrink-0">
            <div>
              <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-gray-500">Oracle Fusion ERP · Vaku AI</div>
              <h3 className="text-2xl font-black tracking-tight mt-0.5 flex items-center gap-2">
                <Receipt size={20} weight="bold" className="text-[#0020C2]" />
                {agent?.label || "AR Receipt Creation"}
              </h3>
              <p className="text-xs text-gray-500 mt-0.5">{agent?.hint}</p>
            </div>
            <div className="flex items-center gap-3 shrink-0">
              {chatStarted && (
                <>
                  <ChatHistoryControl agentKey={openAgent} currentSessionId={sessionId} onNewChat={startNewChat} onOpenSession={openSession} />
                  <div className="w-px h-5 bg-gray-200" />
                </>
              )}
              <button onClick={() => { close(); setSetupOpen(true); }}
                className="text-[11px] font-semibold text-gray-500 hover:text-[#0020C2] flex items-center gap-1">
                <Gear size={13} weight="bold" /> {profile?.oracle_username || "Set up profile"}
              </button>
              <button onClick={close}><X size={18} /></button>
            </div>
          </div>

          {/* Body */}
          {!chatStarted ? (
            <>
              {/* Pre-chat: show wizard fields so they are visible and update as the
                  user provides values in chat. No options/dropdowns shown yet. */}
              <div className="flex-1 overflow-y-auto p-5 space-y-4">
                <WizardFields agentKey={openAgent} values={fieldValues}
                  onChange={(updates) => {
                    const safe = { ...updates };
                    if (!fieldValues.receipt_method && (safe.receipt_method === "Manual" || safe.receipt_method === "Bank Transfer Method")) delete safe.receipt_method;
                    if (safe.business_unit && safe.business_unit !== fieldValues.business_unit) safe.business_unit__label = safe.business_unit__label || safe.business_unit;
                    setFieldValues(safe);
                    const WIZARD_TO_STATE = {
                      business_unit: "business_unit", currency: "currency",
                      entered_amount: "entered_amount", receipt_number: "receipt_number",
                      receipt_date: "receipt_date", accounting_date: "accounting_date",
                      receipt_method: "receipt_method",
                      remittance_bank_account: "remittance_bank_account",
                      remittance_bank_deposit_date: "remittance_bank_deposit_date",
                      remittance_bank_name: "remittance_bank_name",
                      remittance_bank_branch: "remittance_bank_branch",
                      comments: "comments",
                    };
                    const syncFields = {};
                    for (const [wk, sk] of Object.entries(WIZARD_TO_STATE)) {
                      const v = safe[wk] || safe[`${wk}__label`];
                      if (v && !isMissingValue(v)) syncFields[sk] = v;
                    }
                    if (Object.keys(syncFields).length > 0) {
                      api.post("/erp-agents/receipt-create/set-fields", { session_id: sessionId, fields: syncFields }).catch(() => {});
                    }
                  }}
                  missingFields={missingFields} onCreateNew={() => {}} fieldErrors={{}} fieldWarnings={{}} />
              </div>
              <ReceiptInputBar inputRef={inputRef} value={input} onChange={handleInputChange}
                onKeyDown={handleKeyDown} onSend={() => sendMessage(input)} busy={busy}
                disableInput={false} />
            </>
          ) : (
            <div className="flex flex-1 overflow-hidden">
              {/* LEFT — chat */}
              <div className="flex flex-col overflow-hidden border-r border-gray-200"
                style={{ width: "60%", animation: "chatFadeIn 0.4s cubic-bezier(0.4,0,0.2,1)" }}>
                {/* FIX 1: QuickActions removed — no buttons above messages */}
                <div className="flex-1 overflow-y-auto px-4 py-4 space-y-3 bg-gray-50">
                  {messages.map((msg) => (
                    <ReceiptChatBubble key={msg.id} msg={msg}
                      businessUnit={fieldValues.business_unit__label || fieldValues.business_unit || ""}
                      sessionId={sessionId}
                      onPdfUploaded={(data) => {
                        // Handle PDF receipt number extraction result
                        const reply = data?.reply || data?.response || "Receipt Number extracted. Please confirm.";
                        const nextUi = data?.next_field_ui || null;
                        if (data?.fields_collected) mergeIntoWizard(data.fields_collected);
                        if (data?.receipt_number && !isMissingValue(data.receipt_number)) {
                          setFieldValues((prev) => ({
                            ...prev,
                            receipt_number: data.receipt_number,
                            receipt_number__label: data.receipt_number,
                          }));
                        }
                        setMessages((prev) => [...prev, {
                          id: Date.now(), role: "assistant", timestamp: ts(),
                          text: reply,
                          next_field_ui: nextUi,
                          result: nextUi ? { next_field_ui: nextUi } : undefined,
                        }]);
                      }}
                      onPdfValidationFail={(reason) => {
                        setMessages((prev) => [...prev, {
                          id: Date.now(), role: "assistant", timestamp: ts(),
                          text: reason,
                        }]);
                      }}
                      onOptionClick={async (opt) => {
                        // FIX 3: capture receipt method options when user clicks a dropdown
                        // Customer search widget selected a customer
                      if (opt?.value === "__customer_selected__" && opt?.customerData) {
                        const cd = opt.customerData;
                        const acct = cd.customer_account_number || cd.customer_name || "";
                        console.log("[customer] selected:", acct, "name:", cd.customer_name);

                        // Update local wizard state
                        setFieldValues((prev) => ({ ...prev, ...cd }));
                        setCustomerData(cd);

                        // Step 1: persist customer to backend Redis state via set-fields
                        api.post("/erp-agents/receipt-create/set-fields", {
                          session_id: sessionId,
                          fields: {
                            customer_name:           cd.customer_name          || "",
                            customer_account_number: cd.customer_account_number || "",
                            customer_party_number:   cd.customer_party_number  || "",
                            customer_site:           cd.customer_site          || "",   // SiteName from LOV
                            customer_site_name:      cd.customer_site          || "",   // alias
                            customer_site_number:    cd.customer_site_number   || "",
                          },
                        }).then((sfRes) => {
                          console.log("[customer] set-fields done, fields_set:", sfRes?.data?.fields_set);
                          // Step 2: advance the backend stage machine.
                          // CRITICAL: do NOT use sendMessage() here — it checks currentMissingField
                          // and may intercept the account number as a value for a different field,
                          // preventing /chat from being called and causing an infinite customer loop.
                          // Instead call /chat directly with a sentinel that the backend recognises.
                          const sentinelMsg = "__CUSTOMER_CONFIRMED__:" + acct;
                          setBusy(true);
                          setMessages((prev) => [
                            ...prev,
                            { id: Date.now(), role: "user", text: cd.customer_name || acct, timestamp: ts() },
                            { id: Date.now() + 1, role: "assistant", loading: true },
                          ]);
                          api.post("/erp-agents/receipt-create/chat", {
                            session_id: sessionId,
                            message:    sentinelMsg,
                          }).then((res) => {
                            const data = res.data;
                            console.log("[customer] chat advance reply:", data?.reply?.slice?.(0,80));
                            if (data?.progress) setCopilotProgress(data.progress);
                            if (data?.fields_collected) mergeIntoWizard(data.fields_collected);
                            setMessages((prev) => {
                              const updated = [...prev];
                              const li = updated.findLastIndex((m) => m.loading);
                              if (li !== -1) updated[li] = {
                                ...updated[li], loading: false,
                                text: data?.reply || "",
                                next_field_ui: data?.next_field_ui || null,
                                result: data,
                              };
                              return updated;
                            });
                          }).catch((err) => {
                            console.error("[customer] chat advance failed:", err?.message);
                            setMessages((prev) => prev.filter((m) => !m.loading));
                          }).finally(() => setBusy(false));
                        }).catch((err) => {
                          console.error("[customer] set-fields failed:", err?.message);
                          // Even if set-fields fails, try to advance using the same direct path
                          setBusy(true);
                          api.post("/erp-agents/receipt-create/chat", {
                            session_id: sessionId,
                            message:    "__CUSTOMER_CONFIRMED__:" + acct,
                          }).then((res) => {
                            const data = res.data;
                            if (data?.progress) setCopilotProgress(data.progress);
                            if (data?.fields_collected) mergeIntoWizard(data.fields_collected);
                            setMessages((prev) => {
                              const updated = [...prev];
                              const li = updated.findLastIndex((m) => m.loading);
                              if (li !== -1) updated[li] = {
                                ...updated[li], loading: false,
                                text: data?.reply || "",
                                next_field_ui: data?.next_field_ui || null,
                                result: data,
                              };
                              return updated;
                            });
                          }).catch(console.error).finally(() => setBusy(false));
                        });
                        return;
                      }
                      if (opt?.value && msg.next_field_ui?.field === "receipt_method" && msg.next_field_ui?.options?.length > 0) {
                          setReceiptMethodOptions(msg.next_field_ui.options);
                        }
                        if (currentMissingField && opt?.value) {
                          const fieldKey = currentMissingField;
                          const normalized = normalizeFieldValue(fieldKey, opt.value);
                          setFieldValues((prev) => ({ ...prev, [fieldKey]: normalized, [`${fieldKey}__label`]: opt.label || opt.value }));
                          setMessages((prev) => [...prev, { id: Date.now(), role: "user", text: opt.label || opt.value, timestamp: ts() }]);
                          setCurrentMissingField(null);
                          api.post("/erp-agents/receipt-create/set-fields", {
                            session_id: sessionId, fields: { [fieldKey]: normalized },
                          })
                            .then((r) => { if (r.data?.fields_collected) mergeIntoWizard(r.data.fields_collected); })
                            .catch(() => {});
                          askNextMissingField(missingQueue);
                        } else {
                          // FIX 3: also store options when user picks from a backend-driven receipt_method dropdown
                          if (msg.next_field_ui?.field === "receipt_method") {
                            setFieldValues((prev) => ({ ...prev, receipt_method: opt.value, receipt_method__label: opt.label || opt.value }));
                            setMessages((prev) => [...prev, { id: Date.now(), role: "user", text: opt.label || opt.value, timestamp: ts() }]);
                            api.post("/erp-agents/receipt-create/set-fields", {
                              session_id: sessionId, fields: { receipt_method: opt.value },
                            }).catch(() => {});
                            api.post("/erp-agents/receipt-create/chat", { session_id: sessionId, message: opt.value })
                              .then(({ data }) => {
                                if (data?.fields_collected) mergeIntoWizard(data.fields_collected);
                                const nextUi = data?.next_field_ui || data?.result?.next_field_ui;
                                if (nextUi?.field === "receipt_method" && nextUi?.options?.length > 0) setReceiptMethodOptions(nextUi.options);
                              }).catch(() => {});
                          } else {
                            sendMessage(opt.value);
                          }
                        }
                      }} />
                  ))}
                  {uiMode === "extraction" && extractedData && (
                    <ReceiptExtractionSummary
                      data={extractedData}
                      businessUnit={fieldValues.business_unit__label || fieldValues.business_unit}
                      onPopulate={() => handlePopulateFields(extractedData)}
                      onReviewEdit={() => setUiMode("review")}
                    />
                  )}
                  {uiMode === "review" && extractedData && (
                    <ReceiptReviewEditor
                      data={{ ...extractedData, ...fieldValues }}
                      onDone={(updated) => {
                        setExtractedData(updated);
                        mergeIntoWizard(updated);
                        const stillMissing = REQUIRED_RECEIPT_FIELDS.filter(
                          (f) => isMissingValue(updated[f]) && isMissingValue(fieldValues[f])
                        );
                        setUiMode(stillMissing.length === 0 ? "summary" : "extraction");
                      }}
                      onBack={() => setUiMode("extraction")}
                    />
                  )}
                  {uiMode === "summary" && (
                    <ReceiptSummary
                      values={fieldValues}
                      onConfirm={handleConfirm}
                      onReviewEdit={() => { setReceiptStatus("review"); setUiMode("review"); }}
                      receiptStatus={receiptStatus}
                      oracleResult={oracleResult}
                    />
                  )}
                  <div ref={messagesEndRef} />
                </div>
                {/* FIX 3: ReceiptMethodPicker now receives BU-scoped options from state */}
                {currentMissingField === "receipt_method" && (
                  <ReceiptMethodPicker
                    options={receiptMethodOptions}
                    onSelect={(opt) => {
                      const fieldKey = "receipt_method";
                      setFieldValues((prev) => ({ ...prev, [fieldKey]: opt, [`${fieldKey}__label`]: opt, receipt_method: opt, receipt_method__label: opt }));
                      setMessages((prev) => [...prev, { id: Date.now(), role: "user", text: opt, timestamp: ts() }]);
                      setCurrentMissingField(null);
                      api.post("/erp-agents/receipt-create/set-fields", { session_id: sessionId, fields: { receipt_method: opt } }).catch(() => {});
                      askNextMissingField(missingQueue);
                    }}
                  />
                )}
                <ReceiptInputBar inputRef={inputRef} value={input} onChange={handleInputChange}
                  onKeyDown={handleKeyDown} onSend={() => sendMessage(input)} busy={busy}
                  onAttach={handleAttachFiles} attaching={attaching} attachedCount={attachedCount}
                  disableInput={currentMissingField === "receipt_method"} />
              </div>
              {/* RIGHT — wizard */}
              <div className="flex flex-col bg-white"
                style={{ width: "40%", minWidth: "360px", animation: "wizardSlideIn 0.45s cubic-bezier(0.4,0,0.2,1)" }}>
                <div className="flex-1 overflow-y-auto p-4 space-y-4">
                  <div className="text-[10px] tracking-[0.2em] uppercase font-bold text-[#0020C2] flex items-center gap-1.5">
                    <Sparkle size={11} weight="fill" /> Wizard fields
                    <span className="text-gray-400 normal-case font-normal tracking-normal ml-1">· auto-filled from document</span>
                  </div>
                  <WizardFields agentKey={openAgent} values={fieldValues}
                    onChange={(updates) => {
                      const safe = { ...updates };
                      if (!fieldValues.receipt_method && (safe.receipt_method === "Manual" || safe.receipt_method === "Bank Transfer Method")) delete safe.receipt_method;
                      setFieldValues(safe);
                      const syncFields = {};
                      const WIZARD_TO_STATE = {
                        business_unit: "business_unit", currency: "currency",
                        entered_amount: "entered_amount", receipt_number: "receipt_number",
                        receipt_date: "receipt_date", accounting_date: "accounting_date",
                        receipt_method: "receipt_method",
                        remittance_bank_account: "remittance_bank_account",
                        remittance_bank_deposit_date: "remittance_bank_deposit_date",
                        remittance_bank_name: "remittance_bank_name",
                        remittance_bank_branch: "remittance_bank_branch",
                        comments: "comments",
                        customer_name: "customer_name",
                        customer_account_number: "customer_account_number",
                        customer_party_number: "customer_party_number",
                        customer_site: "customer_site",
                        customer_site_number: "customer_site_number",
                      };
                      for (const [wizKey, stateKey] of Object.entries(WIZARD_TO_STATE)) {
                        const val = safe[wizKey] || safe[`${wizKey}__label`];
                        if (val && !isMissingValue(val)) syncFields[stateKey] = val;
                      }
                      if (Object.keys(syncFields).length > 0) {
                        api.post("/erp-agents/receipt-create/set-fields", { session_id: sessionId, fields: syncFields }).catch(() => {});
                      }
                    }}
                    missingFields={missingFields} onCreateNew={() => {}} fieldErrors={{}} fieldWarnings={{}} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
    </>
  );
}